import { Appointment } from '@/types/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, Receipt, Calendar, Users, Clock, Loader2, FileText, Pencil, Phone, Mail } from "lucide-react";
import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import CreateAppointmentBill from '@/components/appointment/CreateAppointmentBill';
import { appointmentApi } from '@/api/appointment';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ViewBill from '@/components/billing/ViewBill';
import BlankPrescriptionPrint from '@/components/appointment/BlankPrescriptionPrint';
import { useSearch } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DoctorSearchableSelect } from '@/components/common/DoctorSearchableSelect';
import { doctorApi } from '@/api/doctor';
import type { HospitalStaff } from '@/types/types';
import { DatePicker } from '@/components/ui/date-filter';
import {
    formatIstCalendarDateForApi,
    istDatetimeLocalToIso,
    toIstDatetimeLocalValue,
} from '@/utils/dateUtils';

function isPractoBooking(source?: string | null) {
    return String(source ?? '').trim().toUpperCase() === 'PRACTO';
}

/** Active hospital doctors plus current appointment doctor if not in that list (e.g. inactive). */
function doctorOptionsForEdit(
    hospitalDoctors: HospitalStaff[],
    editAppointment: Appointment | null
): Array<{ id: string; name: string; specialisation?: string | null }> {
    const byId = new Map<string, { id: string; name: string; specialisation?: string | null }>();
    for (const d of hospitalDoctors) {
        byId.set(d.id, { id: d.id, name: d.name, specialisation: d.specialisation });
    }
    if (editAppointment) {
        const id = editAppointment.doctorId ?? editAppointment.doctor?.id;
        if (id && !byId.has(id)) {
            const nm = editAppointment.doctor?.name?.trim();
            byId.set(id, {
                id,
                name: nm || 'Doctor (not in active list)',
                specialisation: editAppointment.doctor?.specialisation,
            });
        }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export default function QueueManagement() {
    const { user, isChainAdmin } = useAuth();
    const canAdminChangeDoctor =
        user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';
    const [activeTab, setActiveTab] = useState<'queue' | 'appointments'>('queue');
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
    const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);
    const [isViewBillDialogOpen, setIsViewBillDialogOpen] = useState(false);
    const [selectedBillId, setSelectedBillId] = useState("");
    const [isBlankPrescriptionOpen, setIsBlankPrescriptionOpen] = useState(false);
    const [selectedAppointmentIdForPrescription, setSelectedAppointmentIdForPrescription] = useState<string | null>(null);
    const [selectedAppointmentForPrescription, setSelectedAppointmentForPrescription] = useState<Appointment | null>(null);
    const [selectedOpdSeqNoForPrescription, setSelectedOpdSeqNoForPrescription] = useState<number | null>(null);
    const queryClient = useQueryClient();
    const [editOpen, setEditOpen] = useState(false);
    const [editAppointment, setEditAppointment] = useState<Appointment | null>(null);
    const [editDoctorId, setEditDoctorId] = useState('');
    const [editScheduledLocal, setEditScheduledLocal] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

    const todayKey = formatIstCalendarDateForApi(new Date());
    const selectedDateKey = formatIstCalendarDateForApi(selectedDate);
    const isSelectedToday = selectedDateKey === todayKey;
    const isSelectedPast = selectedDateKey < todayKey;
    /** Reception: confirm only today. Hospital admin: may confirm past scheduled rows (backfill bill + status). */
    const allowConfirmOnSelectedDay =
        isSelectedToday || (canAdminChangeDoctor && isSelectedPast);

    // Today's appointments — always used for live queue numbers
    const {
        data: todayAppointments = [],
        isLoading: todayAppointmentsLoading,
        isError: todayAppointmentsError,
        refetch: refetchTodayAppointments,
    } = useQuery({
        queryKey: ['appointments', 'by-date', todayKey],
        queryFn: async () => {
            const response = await appointmentApi.getAppointmentsByDate({
                date: todayKey,
            });
            return response.data?.data ?? [];
        },
        staleTime: 30000,
        refetchInterval: 60000,
        refetchOnWindowFocus: true,
    });

    // Selected calendar day — used for the Appointments tab (today or future)
    const {
        data: dayAppointments = [],
        isLoading: dayAppointmentsLoading,
        isError: dayAppointmentsError,
        refetch: refetchDayAppointments,
    } = useQuery({
        queryKey: ['appointments', 'by-date', selectedDateKey],
        queryFn: async () => {
            const response = await appointmentApi.getAppointmentsByDate({
                date: selectedDateKey,
            });
            return response.data?.data ?? [];
        },
        staleTime: 30000,
        refetchInterval: isSelectedToday ? 60000 : false,
        refetchOnWindowFocus: true,
        enabled: !!selectedDateKey,
    });

    const appointmentsLoading = todayAppointmentsLoading || (!isSelectedToday && dayAppointmentsLoading);
    const appointmentsError = todayAppointmentsError || (!isSelectedToday && dayAppointmentsError);
    const refetchAppointments = async () => {
        await Promise.all([refetchTodayAppointments(), refetchDayAppointments()]);
    };

    const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
        queryKey: ['queue-management-doctors'],
        queryFn: () => doctorApi.getDoctorsByHospital(),
        staleTime: 60_000,
    });

    const doctorsForEdit = useMemo(
        () => doctorOptionsForEdit(doctors, editOpen ? editAppointment : null),
        [doctors, editOpen, editAppointment]
    );

    const updateScheduleMut = useMutation({
        mutationFn: async (payload: { id: string; scheduledAtIso: string; doctorId: string }) => {
            await appointmentApi.updateAppointmentSchedule(payload.id, {
                scheduledAt: payload.scheduledAtIso,
                doctorId: payload.doctorId,
            });
        },
        onSuccess: async () => {
            toast.success('Appointment updated');
            await queryClient.invalidateQueries({ queryKey: ['appointments', 'by-date'] });
            await queryClient.invalidateQueries({ queryKey: ['appointments'] });
            setEditOpen(false);
            setEditAppointment(null);
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message || err?.message || 'Could not update appointment');
        },
    });

    const openEditDialog = (appointment: Appointment) => {
        setEditAppointment(appointment);
        setEditDoctorId(appointment.doctorId ?? appointment.doctor?.id ?? '');
        setEditScheduledLocal(toIstDatetimeLocalValue(appointment.scheduledAt));
        setEditOpen(true);
    };

    const { searchQuery } = useSearch();

    const filterBySearch = (list: Appointment[]) =>
        list.filter((appointment) => {
            if (!searchQuery) return true;
            const searchLower = searchQuery.toLowerCase();
            return (
                appointment?.patient?.name?.toLowerCase().includes(searchLower) ||
                appointment?.patient?.phone?.toLowerCase().includes(searchLower) ||
                appointment?.doctor?.name?.toLowerCase().includes(searchLower) ||
                appointment.visitType.toLowerCase().includes(searchLower) ||
                appointment.status.toLowerCase().includes(searchLower)
            );
        });

    const filteredTodayAppointments = filterBySearch(todayAppointments as Appointment[]);
    const filteredDayAppointments = filterBySearch(dayAppointments as Appointment[]);
    
    const qualifiesAutoZeroOpdConfirm = (appt: Appointment | undefined) => {
        if (!appt || appt.visitType !== 'OPD') return false;
        const tier = String(appt.opdConsultationTier ?? '').toUpperCase();
        if (tier === 'FREE') return true;
        const listFee = Number(appt.doctor?.opdCharge?.amount);
        return Number.isFinite(listFee) && listFee <= 0;
    };

    const handleConfirmAppointment = (appointmentId: string) => {
        if (!allowConfirmOnSelectedDay) {
            toast.info(
                canAdminChangeDoctor && !isSelectedPast && !isSelectedToday
                    ? 'Confirm is only for today or past dates. Pick the visit date or use Today when the patient arrives.'
                    : 'Confirm is only available for today’s appointments. Switch the date to today when the patient arrives.'
            );
            return;
        }
        const appt = (dayAppointments as Appointment[])?.find((a) => a.id === appointmentId);
        const existingBillId = appt?.bills?.[0]?.id;

        if (qualifiesAutoZeroOpdConfirm(appt)) {
            if (existingBillId && appt?.status === 'CONFIRMED') {
                setSelectedBillId(existingBillId);
                setIsViewBillDialogOpen(true);
                return;
            }
            confirmFreeOpdMut.mutate(appointmentId);
            return;
        }

        if (existingBillId) {
            setSelectedBillId(existingBillId);
            setIsViewBillDialogOpen(true);
            toast.info("Bill already exists for this appointment");
            return;
        }

        setSelectedAppointmentId(appointmentId);
        setIsBillDialogOpen(true);
    };

    const handleCloseBillDialog = () => {
        setIsBillDialogOpen(false);
        setSelectedAppointmentId(null);
    };

    // Function to handle successful appointment confirmation
    const handleAppointmentConfirmed = async () => {
        try {
            await queryClient.invalidateQueries({
                queryKey: ['appointments', 'by-date']
            });
            await queryClient.invalidateQueries({
                queryKey: ['appointments']
            });

            toast.success('Appointment confirmed and added to queue!');
            handleCloseBillDialog();
        } catch (error) {
            console.error('Error refreshing appointments:', error);
            toast.error('Appointment confirmed but failed to refresh queue. Please refresh manually.');
        }
    };

    const confirmFreeOpdMut = useMutation({
        mutationFn: (appointmentId: string) => appointmentApi.confirmFreeCrmOpd(appointmentId),
        onSuccess: async () => {
            try {
                await queryClient.invalidateQueries({ queryKey: ['appointments', 'by-date'] });
                await queryClient.invalidateQueries({ queryKey: ['appointments'] });
                toast.success('OPD confirmed — zero-value bill created');
            } catch (error) {
                console.error('Error refreshing appointments:', error);
                toast.error('Confirmed but failed to refresh queue. Please refresh manually.');
            }
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message || err?.message || 'Could not confirm free OPD');
        },
    });

    const handleViewBill = (appointment: Appointment) => {
        setSelectedBillId(appointment.bills?.[0]?.id || '');
        setIsViewBillDialogOpen(true);
    };

    const handleCloseViewBillDialog = () => {
        setIsViewBillDialogOpen(false);
        setSelectedBillId("");
    };

    const handlePrintBlankPrescription = (appointment: Appointment) => {
        setSelectedAppointmentIdForPrescription(appointment.id);
        setSelectedAppointmentForPrescription(appointment);
        // Use the computed OPD queue sequence number when available (Queue tab).
        setSelectedOpdSeqNoForPrescription(confirmedQueueMap.get(appointment.id) ?? null);
        setIsBlankPrescriptionOpen(true);
    };

    const handleCloseBlankPrescription = () => {
        setIsBlankPrescriptionOpen(false);
        setSelectedAppointmentIdForPrescription(null);
        setSelectedAppointmentForPrescription(null);
        setSelectedOpdSeqNoForPrescription(null);
    };


    // Queue is always today's confirmed; scheduled list follows the selected date
    const confirmedAppointments = filteredTodayAppointments.filter(
        (appointment: Appointment) => appointment.status === 'CONFIRMED'
    );

    const scheduledAppointments = filteredDayAppointments.filter(
        (appointment: Appointment) =>
            appointment.status !== 'CONFIRMED' && appointment.status !== 'CANCELLED'
    );

    

    // Function to assign queue numbers
    const assignQueueNumbers = (appointments: Appointment[] | undefined) => {
        if (!appointments) return new Map();

        // Sort appointments by their confirmation time (updatedAt)
        const sortedAppointments = [...appointments].sort((a, b) => {
            const dateA = new Date(a.updatedAt);
            const dateB = new Date(b.updatedAt);
            return dateA.getTime() - dateB.getTime();
        });

        // Create a map of appointment IDs to queue numbers
        const queueMap = new Map<string, number>();
        sortedAppointments.forEach((appointment, index) => {
            queueMap.set(appointment.id, index + 1);
        });

        return queueMap;
    };

    // Generate queue numbers for confirmed appointments
    const confirmedQueueMap = assignQueueNumbers(confirmedAppointments);

    // Sort confirmed appointments by queue number
    const sortedConfirmedAppointments = confirmedAppointments ? [...confirmedAppointments].sort((a, b) => {
        const queueA = confirmedQueueMap.get(a.id) || 0;
        const queueB = confirmedQueueMap.get(b.id) || 0;
        return queueA - queueB;
    }) : [];

    // Calculate stats
    const totalScheduled = scheduledAppointments.length;
    const totalInQueue = confirmedAppointments.length;
    type DoctorQueueGroup = { doctorName: string; appointments: Appointment[] };
    const doctorQueueGroups: Record<string, DoctorQueueGroup> = sortedConfirmedAppointments.reduce((acc, appointment) => {
        const doctorId = appointment.doctor?.id || 'unknown';
        const doctorName = ensureDoctorPrefix(appointment.doctor?.name || 'Unknown Doctor');
        if (!acc[doctorId]) {
            acc[doctorId] = { doctorName, appointments: [] as Appointment[] };
        }
        acc[doctorId].appointments.push(appointment);
        return acc;
    }, {} as Record<string, DoctorQueueGroup>);

    const doctorQueueSections = (Object.entries(doctorQueueGroups) as Array<[string, DoctorQueueGroup]>).filter(
        ([, group]) => group.appointments.length > 0
    );

    if (appointmentsLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                    <p className="text-gray-600">Loading appointments...</p>
                </div>
            </div>
        );
    }

    if (appointmentsError) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Appointments</h3>
                    <p className="text-gray-600 mb-4">There was an error loading appointments.</p>
                    <Button
                        onClick={() => refetchAppointments()}
                        variant="outline"
                        className="gap-2"
                    >
                        <Calendar className="h-4 w-4" />
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    const AppointmentTable = ({
        appointments,
        title,
        showQueue = false,
        enableScheduledEdit = false,
        enableAdminQueueEdit = false,
        allowConfirm = true,
        showHospitalColumn = false,
    }: {
        appointments: Appointment[] | undefined;
        title: string;
        showQueue?: boolean;
        /** Show Edit (doctor + time) for SCHEDULED rows — e.g. Today's scheduled list */
        enableScheduledEdit?: boolean;
        /** Hospital admin: change doctor/time on confirmed queue appointments */
        enableAdminQueueEdit?: boolean;
        /** Confirm (bill + queue): today for all roles; past dates for hospital admin */
        allowConfirm?: boolean;
        showHospitalColumn?: boolean;
    }) => (
        <Card className="shadow-sm">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        {showQueue ? <Users className="h-5 w-5 text-blue-600" /> : <Calendar className="h-5 w-5 text-green-600" />}
                        {title}
                    </CardTitle>
                    <Badge variant="secondary" className="text-sm">
                        {appointments?.length || 0} {appointments?.length === 1 ? 'appointment' : 'appointments'}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="rounded-lg border">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-gray-50/50">
                                {showQueue && <TableHead className="text-gray-700 font-medium">Queue #</TableHead>}
                                <TableHead className="text-gray-700 font-medium">Patient Name</TableHead>
                                {showHospitalColumn && (
                                    <TableHead className="text-gray-700 font-medium">Hospital</TableHead>
                                )}
                                <TableHead className="text-gray-700 font-medium">Doctor Name</TableHead>
                                <TableHead className="text-gray-700 font-medium">Visit Type</TableHead>
                                <TableHead className="text-gray-700 font-medium">Scheduled At</TableHead>
                                <TableHead className="text-gray-700 font-medium">Booking Source</TableHead>
                                <TableHead className="text-gray-700 font-medium">Created By</TableHead>
                                <TableHead className="text-gray-700 font-medium">Status</TableHead>
                                <TableHead className="text-gray-700 font-medium">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(!appointments || appointments.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={(showQueue ? 9 : 8) + (showHospitalColumn ? 1 : 0)} className="text-center py-12">
                                        <div className="flex flex-col items-center gap-2">
                                            {showQueue ?
                                                <Users className="h-8 w-8 text-gray-400" /> :
                                                <Calendar className="h-8 w-8 text-gray-400" />
                                            }
                                            <p className="text-gray-500 font-medium">
                                                {showQueue ? 'No appointments in queue' : 'No scheduled appointments'}
                                            </p>
                                            <p className="text-gray-400 text-sm">
                                                {showQueue ?
                                                    'Confirmed appointments will appear here' :
                                                    'New appointments will appear here when scheduled'
                                                }
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                appointments.map((appointment) => (
                                    <TableRow key={appointment.id} className="hover:bg-gray-50/50 transition-colors">
                                        {showQueue && (
                                            <TableCell className="font-bold text-lg text-blue-600">
                                                #{confirmedQueueMap.get(appointment.id)}
                                            </TableCell>
                                        )}
                                        <TableCell className="font-medium text-gray-900">
                                            <div>
                                                {appointment.patient?.name || 'N/A'}
                                                {isPractoBooking((appointment as any).source) && (
                                                    <div className="mt-1 space-y-0.5 text-xs font-normal text-gray-600">
                                                        {appointment.patient?.phone ? (
                                                            <div className="flex items-center gap-1.5">
                                                                <Phone className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
                                                                <span className="font-mono">{appointment.patient.phone}</span>
                                                            </div>
                                                        ) : null}
                                                        {appointment.patient?.email ? (
                                                            <div className="flex items-center gap-1.5 min-w-0">
                                                                <Mail className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
                                                                <span className="truncate" title={appointment.patient.email}>
                                                                    {appointment.patient.email}
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        {showHospitalColumn && (
                                            <TableCell className="text-gray-700 text-sm">
                                                {appointment.hospital?.name ?? '—'}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-gray-700">{ensureDoctorPrefix(appointment.doctor?.name || 'N/A')}</TableCell>
                                        <TableCell className="text-gray-700">{appointment.visitType}</TableCell>
                                        <TableCell className="text-gray-600">
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(appointment.scheduledAt).toLocaleString('en-GB', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    timeZone: 'Asia/Kolkata'
                                                })}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-gray-700">
                                            <div className="flex items-center gap-1">
                                                <Badge 
                                                    variant="outline" 
                                                    className="text-xs"
                                                >
                                                    {(appointment as any).source || 'INTERNAL'}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-gray-700 text-sm">
                                            {appointment.creator?.name?.trim()
                                                ? appointment.creator.name.trim()
                                                : (() => {
                                                      const src = String(appointment.source || '').toUpperCase();
                                                      if (src === 'PRACTO') return 'Practo';
                                                      if (
                                                          src === 'GOOGLE' ||
                                                          src === 'WEBSITE' ||
                                                          src === 'GMB' ||
                                                          src === 'CUSTOM'
                                                      ) {
                                                          return 'Online';
                                                      }
                                                      return '—';
                                                  })()}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    appointment.status === 'SCHEDULED' ? 'default' :
                                                        appointment.status === 'CONFIRMED' ? 'secondary' :
                                                            appointment.status === 'CANCELLED' ? 'destructive' :
                                                                'outline'
                                                }
                                                className="text-xs"
                                            >
                                                {appointment.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap items-center gap-1">
                                            {enableScheduledEdit && !showQueue && appointment.status === 'SCHEDULED' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openEditDialog(appointment)}
                                                    className="gap-1"
                                                >
                                                    <Pencil className="w-3 h-3" />
                                                    Edit
                                                </Button>
                                            )}
                                            {enableAdminQueueEdit &&
                                                showQueue &&
                                                appointment.status === 'CONFIRMED' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openEditDialog(appointment)}
                                                    className="gap-1"
                                                >
                                                    <Pencil className="w-3 h-3" />
                                                    Change doctor
                                                </Button>
                                            )}
                                            {!showQueue && (appointment as any).bills?.[0]?.id && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleViewBill(appointment)}
                                                    className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                >
                                                    <Receipt className="w-3 h-3" />
                                                    Open bill
                                                </Button>
                                            )}
                                            {!showQueue && appointment.status === 'SCHEDULED' && allowConfirm && (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={() => handleConfirmAppointment(appointment.id)}
                                                    disabled={confirmFreeOpdMut.isPending}
                                                    className="gap-1 bg-green-600 hover:bg-green-700"
                                                >
                                                    {confirmFreeOpdMut.isPending ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <CheckCircle className="w-3 h-3" />
                                                    )}
                                                    {qualifiesAutoZeroOpdConfirm(appointment)
                                                        ? 'Confirm (₹0 bill)'
                                                        : 'Confirm'}
                                                </Button>
                                            )}
                                            {showQueue && appointment.status === 'CONFIRMED' && (appointment as any).bills?.[0] && (
                                                <div className="flex flex-wrap items-center gap-1">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleViewBill(appointment)}
                                                        className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                    >
                                                        <Receipt className="w-3 h-3" />
                                                        View Bill
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handlePrintBlankPrescription(appointment)}
                                                        className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                    >
                                                        <FileText className="w-3 h-3" />
                                                        Print Blank Prescription
                                                    </Button>
                                                </div>
                                            )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
    return (
        <div className="thms-page-shell">
            {/* Header with Stats */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="thms-page-title">Queue Management</h1>
                    <p className="thms-page-subtitle">Manage appointment queue and confirmations — pick a date to view future bookings</p>
                </div>

                <div className="flex gap-4">
                    <Card className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-blue-600">{totalInQueue}</div>
                                <div className="text-sm text-gray-600">In Queue</div>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Calendar className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-green-600">{totalScheduled}</div>
                                <div className="text-sm text-gray-600">{isSelectedToday ? 'Scheduled today' : 'Scheduled'}</div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Date filter + Tab Navigation */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Appointments date:</span>
                    <DatePicker
                        date={selectedDate}
                        onDateChange={(date) => {
                            if (date) {
                                setSelectedDate(date);
                                setActiveTab('appointments');
                            }
                        }}
                        className="w-[240px]"
                    />
                    {!isSelectedToday && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedDate(new Date())}
                        >
                            Today
                        </Button>
                    )}
                </div>
            </div>

            <div className="border-b border-gray-200">
                <nav className="-mb-px flex" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('queue')}
                        className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'queue'
                            ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Users className="h-4 w-4" />
                            Queue Numbers
                            {totalInQueue > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">
                                    {totalInQueue}
                                </Badge>
                            )}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('appointments')}
                        className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'appointments'
                            ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {isSelectedToday ? "Today's Appointments" : 'Appointments'}
                            {totalScheduled > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">
                                    {totalScheduled}
                                </Badge>
                            )}
                        </div>
                    </button>
                </nav>
            </div>

            {/* Appointment Tables */}
            <div>
                {activeTab === 'queue' ? (
                    doctorQueueSections.length === 0 ? (
                        <AppointmentTable
                            appointments={[]}
                            title="Today's Queue"
                            showQueue={true}
                            showHospitalColumn={!!isChainAdmin}
                        />
                    ) : (
                        <div className="space-y-4">
                            {doctorQueueSections.map(([doctorId, group]) => (
                                <AppointmentTable
                                    key={doctorId}
                                    appointments={group.appointments}
                                    title={`Queue for ${group.doctorName}`}
                                    showQueue={true}
                                    enableAdminQueueEdit={canAdminChangeDoctor}
                                    showHospitalColumn={!!isChainAdmin}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <AppointmentTable
                        appointments={scheduledAppointments}
                        title={
                            isSelectedToday
                                ? "Today's Scheduled Appointments"
                                : `Scheduled Appointments — ${format(selectedDate, 'PPP')}`
                        }
                        enableScheduledEdit
                        allowConfirm={allowConfirmOnSelectedDay}
                        showHospitalColumn={!!isChainAdmin}
                    />
                )}
            </div>

            {/* Dialogs */}
            <CreateAppointmentBill
                isOpen={isBillDialogOpen}
                onClose={handleCloseBillDialog}
                appointmentId={selectedAppointmentId ?? ""}
                onSuccess={handleAppointmentConfirmed}
            />

            <ViewBill
                isOpen={isViewBillDialogOpen}
                onClose={handleCloseViewBillDialog}
                billId={selectedBillId}
            />

            <BlankPrescriptionPrint
                isOpen={isBlankPrescriptionOpen}
                onClose={handleCloseBlankPrescription}
                appointmentId={selectedAppointmentIdForPrescription}
                initialAppointment={selectedAppointmentForPrescription}
                sequenceNo={selectedOpdSeqNoForPrescription}
            />

            <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditAppointment(null); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editAppointment?.status === 'CONFIRMED' ? 'Change doctor / time' : 'Edit appointment'}
                        </DialogTitle>
                    </DialogHeader>
                    {editAppointment && (
                        <div className="space-y-4 py-2">
                            <p className="text-sm text-muted-foreground">
                                Patient: <span className="font-medium text-foreground">{editAppointment.patient?.name ?? '—'}</span>
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="edit-doctor">Doctor</Label>
                                <DoctorSearchableSelect
                                    id="edit-doctor"
                                    doctors={doctorsForEdit}
                                    value={editDoctorId}
                                    onValueChange={setEditDoctorId}
                                    disabled={doctorsLoading}
                                    placeholder={doctorsLoading ? 'Loading doctors…' : 'Select doctor'}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-when">Scheduled date & time</Label>
                                <Input
                                    id="edit-when"
                                    type="datetime-local"
                                    value={editScheduledLocal}
                                    onChange={(e) => setEditScheduledLocal(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => { setEditOpen(false); setEditAppointment(null); }}>
                            Cancel
                        </Button>
                        <Button
                            disabled={
                                updateScheduleMut.isPending ||
                                !editAppointment ||
                                !editDoctorId ||
                                !editScheduledLocal.trim()
                            }
                            onClick={() => {
                                if (!editAppointment || !editDoctorId || !editScheduledLocal.trim()) return;
                                const iso = istDatetimeLocalToIso(editScheduledLocal);
                                if (!iso) {
                                    toast.error('Please enter a valid date and time.');
                                    return;
                                }
                                updateScheduleMut.mutate({
                                    id: editAppointment.id,
                                    scheduledAtIso: iso,
                                    doctorId: editDoctorId,
                                });
                            }}
                        >
                            {updateScheduleMut.isPending ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                                </span>
                            ) : (
                                'Save changes'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
